/* =============================================================================
   Turnstile.jsx  —  renders a Cloudflare Turnstile widget and reports its token
   via onToken. The token is passed on the public form POSTs and verified by the
   Worker. Uses explicit rendering (script loaded in index.html).
============================================================================= */
import { useEffect, useRef } from 'react';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

function whenReady(cb) {
  if (window.turnstile) return cb();
  const timer = setInterval(() => {
    if (window.turnstile) {
      clearInterval(timer);
      cb();
    }
  }, 150);
  setTimeout(() => clearInterval(timer), 10000);
}

export function Turnstile({ onToken, action }) {
  const elRef = useRef(null);
  const widgetRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    let mounted = true;
    whenReady(() => {
      if (!mounted || !elRef.current || widgetRef.current !== null || !window.turnstile) return;
      widgetRef.current = window.turnstile.render(elRef.current, {
        sitekey: SITE_KEY,
        action,
        // Stay invisible unless a challenge is actually required, so passing
        // guests never see a widget box.
        appearance: 'interaction-only',
        callback: (token) => onTokenRef.current(token),
        'error-callback': () => onTokenRef.current(''),
        'expired-callback': () => onTokenRef.current(''),
      });
    });
    return () => {
      mounted = false;
      if (widgetRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetRef.current);
        } catch {
          /* widget already gone */
        }
        widgetRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={elRef} className="turnstile" style={{ marginTop: 12 }} />;
}
